import { useCallback, useEffect, useState } from 'react';
import { Link, Title } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import { fmtDate } from '../../shared/Panel';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import { menuLabel } from '../menuConfig';
import { FeaturesLoading, useFeature, useFeatureState } from '../featureFlags/FeatureGate';
import {
  DEPARTMENT_LABELS,
  FEATURE_NAMED_NOTIFICATIONS,
  FEATURE_NOTIFICATION_READ_STATE,
  FEATURE_VARIANT_NOTIFICATIONS,
  audienceText,
  fetchNotificationHistory,
  historyFiltersFromAddress,
  markNotificationRead,
  markNotificationsRead,
  type HistoryFilters,
  type NotificationHistory as History,
} from './notificationsApi';
import { StaleChip, SYSTEM_AUTHOR } from './NotificationBell';
import { hashQueryParams } from '../../shared/hashQuery';

/**
 * Strona „Powiadomienia” z historią (15.10.2026, Etap 1 makiety FTL p.
 * Mariusza 13.10 — `powiadomienia.html`; decyzja Bartka 15.10.2026).
 *
 * Te same powiadomienia co dzwonek (reguła działowa w API bez zmian), ale
 * z 7, 14, 30 albo 90 dni i z filtrami: dział, rodzaj, przeczytane /
 * nieprzeczytane; numer zlecenia prowadzi do karty. Front nie zna reguły —
 * pokazuje to, co przyszło. Powiadomienia imienne (o „Twoich” wariantach
 * i wnioskach) — od 20.10.2026 za przełącznikiem `variantNotifications`
 * (makieta back office v2), z filtrem „Tylko moje”; bez niego strona mówi
 * o działach, jak dotąd.
 *
 * Za przełącznikiem `notificationsPage` — wyłączony: zamiast strony
 * komunikat (pozycji w menu i odnośnika w dzwonku wtedy nie ma).
 */

export const HISTORY_NOTE =
  'Powiadomienia działów FTL i Trade z historii zmian zleceń — te same co w dzwonku, z dłuższego okresu. Twoje własne zmiany się tu nie pojawiają. Powiadomień imiennych (np. o Twoim wariancie) jeszcze nie ma.';

/**
 * Z powiadomieniami imiennymi (20.10.2026, przełącznik `variantNotifications`)
 * — zamiast zdania, że ich nie ma.
 */
export const HISTORY_NOTE_PERSONAL =
  'Powiadomienia działów FTL i Trade z historii zmian zleceń — te same co w dzwonku, z dłuższego okresu — oraz powiadomienia imienne o Twoich wariantach i wnioskach. Twoje własne zmiany się tu nie pojawiają.';

/**
 * Z powiadomieniami imiennymi z makiety FTL (24.10.2026, przełącznik
 * `namedNotifications`) — pełniejsza lista tego, co jest „Twoje”.
 */
export const HISTORY_NOTE_NAMED =
  'Powiadomienia działów FTL i Trade z historii zmian zleceń — te same co w dzwonku, z dłuższego okresu — oraz powiadomienia imienne: o Twoich wariantach i wnioskach, o pojazdach, których jesteś opiekunem FTL, o zleceniach przekazanych przez Ciebie do Trade, a dla Team Leadera — o ryzyku i przekazaniach między FTL i Trade. Twoje własne zmiany się tu nie pojawiają.';

export const FEATURE_OFF_NOTE =
  'Strona powiadomień jest wyłączona w wersji aplikacji, z której korzystasz. Powiadomienia z ostatnich dwóch tygodni są w dzwonku na górnym pasku.';

/** Nazwy filtra działu — `followed` i `all` to wartości API, nie tłumaczymy. */
export const DEPARTMENT_FILTER_LABELS: Record<string, string> = {
  followed: 'obserwowane (jak w dzwonku)',
  all: 'wszystkie działy',
};

export const READ_FILTER_LABELS: Record<HistoryFilters['read'], string> = {
  all: 'wszystkie',
  unread: 'nieprzeczytane',
  read: 'przeczytane',
};

/** „Aktualność” (24.10.2026, `notificationReadState`) — wartości API, nie tłumaczymy. */
export const CURRENT_FILTER_LABELS: Record<NonNullable<HistoryFilters['current']>, string> = {
  all: 'wszystkie',
  current: 'aktualne',
  stale: 'nieaktualne',
};

const PER_PAGE = 25;

/** „Wszystkie rodzaje” w polu wyboru — do API idzie pusty filtr. */
const ANY_KIND = 'wszystkie';

export const NotificationHistory = () => {
  const on = useFeatureState('notificationsPage');
  if (on === undefined) return <FeaturesLoading />;
  if (!on) {
    return (
      <Box sx={{ p: 2 }}>
        <Title title={menuLabel('notifications')} />
        <Alert severity="info">{FEATURE_OFF_NOTE}</Alert>
      </Box>
    );
  }
  return <HistoryPage />;
};

function HistoryPage() {
  const variantsOn = useFeature(FEATURE_VARIANT_NOTIFICATIONS);
  // Imienne z makiety FTL (24.10.2026) — „Tylko moje” także bez imiennych o wariantach.
  const namedOn = useFeature(FEATURE_NAMED_NOTIFICATIONS);
  const personalOn = variantsOn || namedOn;
  // „Przeczytane” osobno i „nieaktualne” (24.10.2026, S-24.10-09).
  const readStateOn = useFeature(FEATURE_NOTIFICATION_READ_STATE);
  // Filtry z adresu — kafelek „Moje powiadomienia” pulpitu planisty
  // (20.10.2026); bez parametrów — domyślne, jak dotąd.
  const [filters, setFilters] = useState<HistoryFilters>(() =>
    historyFiltersFromAddress(hashQueryParams()),
  );
  const [data, setData] = useState<History | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await fetchNotificationHistory(filters));
      setError(null);
    } catch (e) {
      setError(toErrorMessage(e));
    }
  }, [filters]);

  useEffect(() => {
    void load();
  }, [load]);

  const change = (patch: Partial<HistoryFilters>) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(0);
  };

  const readAll = async () => {
    setBusy(true);
    try {
      await markNotificationsRead();
      await load();
    } catch (e) {
      setError(toErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const readOne = async (key: string | undefined) => {
    if (!readStateOn || !key) return;
    try {
      await markNotificationRead(key);
      await load();
    } catch (e) {
      setError(toErrorMessage(e));
    }
  };

  const departments = data?.departments ?? Object.keys(DEPARTMENT_LABELS);
  const periods = data?.periods ?? [7, 14, 30, 90];
  const rows = (data?.items ?? []).slice(page * PER_PAGE, (page + 1) * PER_PAGE);

  return (
    <Box sx={{ p: 2 }}>
      <Title title={menuLabel('notifications')} />
      <Alert severity="info" sx={{ mb: 2 }}>
        {namedOn ? HISTORY_NOTE_NAMED : personalOn ? HISTORY_NOTE_PERSONAL : HISTORY_NOTE}
      </Alert>
      <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: 'center', flexWrap: 'wrap' }} useFlexGap>
        <FilterSelect
          id="powiadomienia-okres"
          label="Okres"
          value={String(filters.days)}
          options={periods.map((d) => [String(d), `ostatnie ${d} dni`])}
          onChange={(v) => change({ days: Number(v) })}
        />
        <FilterSelect
          id="powiadomienia-dzial"
          label="Dział"
          value={filters.department}
          options={[
            ['followed', DEPARTMENT_FILTER_LABELS.followed],
            ['all', DEPARTMENT_FILTER_LABELS.all],
            ...departments.map((d): [string, string] => [d, DEPARTMENT_LABELS[d] ?? d]),
          ]}
          onChange={(v) => change({ department: v })}
        />
        <FilterSelect
          id="powiadomienia-rodzaj"
          label="Rodzaj"
          value={filters.kind || ANY_KIND}
          minWidth={260}
          options={[
            [ANY_KIND, 'wszystkie'],
            ...(data?.kinds ?? []).map((k): [string, string] => [k.kind, k.label]),
          ]}
          onChange={(v) => change({ kind: v === ANY_KIND ? '' : v })}
        />
        <FilterSelect
          id="powiadomienia-stan"
          label="Stan"
          value={filters.read}
          options={Object.entries(READ_FILTER_LABELS)}
          onChange={(v) => change({ read: v as HistoryFilters['read'] })}
        />
        {readStateOn && (
          <FilterSelect
            id="powiadomienia-aktualnosc"
            label="Aktualność"
            value={filters.current ?? 'all'}
            options={Object.entries(CURRENT_FILTER_LABELS)}
            onChange={(v) => change({ current: v as HistoryFilters['current'] })}
          />
        )}
        {personalOn && (
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={filters.mine === true}
                onChange={(e) => change({ mine: e.target.checked })}
              />
            }
            label="Tylko moje"
          />
        )}
        <Box sx={{ flex: 1 }} />
        {/* „Wszystkie” — także te spoza bieżących filtrów, jak w dzwonku. */}
        <Button size="small" onClick={() => void readAll()} disabled={busy}>
          Oznacz wszystkie jako przeczytane
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {data?.truncated && (
        <Alert severity="warning" sx={{ mb: 2 }} data-testid="history-truncated">
          {data.coveredFrom
            ? `W wybranym okresie jest więcej zdarzeń, niż mieści jedna lista — pokazujemy pełną historię od ${fmtDate(data.coveredFrom)}. Zawęź rodzaj albo okres, żeby zobaczyć starsze.`
            : 'Pasujących powiadomień jest więcej, niż mieści jedna lista — zawęź filtry.'}
        </Alert>
      )}

      {data === null && !error ? (
        <CircularProgress size={24} />
      ) : data && data.items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Brak powiadomień w wybranym okresie i przy tych filtrach.
        </Typography>
      ) : data ? (
        <>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
            Pasujących: {data.total}, w tym nieprzeczytanych: {data.unread}.
          </Typography>
          <Table size="small" aria-label="Historia powiadomień">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 140 }}>Kiedy</TableCell>
                <TableCell sx={{ width: 160 }}>Zlecenie</TableCell>
                <TableCell>Powiadomienie</TableCell>
                <TableCell sx={{ width: 140 }}>Kto</TableCell>
                <TableCell sx={{ width: 90 }}>Dla działu</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((i) => (
                <TableRow
                  key={i.id}
                  data-testid={i.unread ? 'history-unread' : 'history-read'}
                  sx={i.important ? { '& td:first-of-type': { borderLeft: 3, borderLeftColor: 'error.main' } } : undefined}
                >
                  <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: i.unread ? 600 : 400 }}>
                    {fmtDate(i.at)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Link
                      to={`/user-offers/${i.order.id}/show`}
                      onClick={() => {
                        if (i.unread) void readOne(i.key);
                      }}
                    >
                      {i.order.externalId ?? `#${i.order.id}`}
                    </Link>
                  </TableCell>
                  <TableCell sx={{ fontWeight: i.unread ? 600 : 400 }}>
                    {i.important ? (
                      <ErrorOutlineRoundedIcon
                        aria-label="Ważne"
                        sx={{ fontSize: 16, color: 'error.main', verticalAlign: 'text-bottom', mr: 0.5 }}
                      />
                    ) : null}
                    {i.text}
                    {readStateOn && i.stale ? <StaleChip testId="history-stale" /> : null}
                    {readStateOn && i.unread && i.key ? (
                      <Button
                        size="small"
                        sx={{ py: 0, ml: 0.5, minWidth: 0, fontSize: 12 }}
                        onClick={() => void readOne(i.key)}
                        data-testid="history-mark-read"
                      >
                        Oznacz jako przeczytane
                      </Button>
                    ) : null}
                  </TableCell>
                  <TableCell>{i.by ?? SYSTEM_AUTHOR}</TableCell>
                  <TableCell>{audienceText(i)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={data.items.length}
            page={page}
            onPageChange={(_e, p) => setPage(p)}
            rowsPerPage={PER_PAGE}
            rowsPerPageOptions={[PER_PAGE]}
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} z ${count}`}
          />
        </>
      ) : null}
    </Box>
  );
}

function FilterSelect({
  id,
  label,
  value,
  options,
  onChange,
  minWidth = 160,
}: {
  id: string;
  label: string;
  value: string;
  options: Array<[string, string]>;
  onChange: (value: string) => void;
  minWidth?: number;
}) {
  return (
    <FormControl size="small" fullWidth={false} sx={{ minWidth }}>
      <InputLabel id={`${id}-label`}>{label}</InputLabel>
      <Select
        labelId={`${id}-label`}
        label={label}
        value={value}
        onChange={(e) => onChange(String(e.target.value))}
      >
        {options.map(([v, text]) => (
          <MenuItem key={v} value={v}>
            {text}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
